/**
 * RCAList — listă RCA (read-only + filtre). Fără creare din listă (PRD clarificare v1).
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileSearch, AlertTriangle } from 'lucide-react';
import { useRCA } from '../../hooks/useRCA';
import RCAForm from './RCAForm';
import './RCAList.css';

const STATUS_OPTIONS = [
  { value: '', labelKey: 'filterAll' },
  { value: 'open', labelKey: 'statusOpen' },
  { value: 'under_investigation', labelKey: 'statusUnderInvestigation' },
  { value: 'resolved', labelKey: 'statusResolved' },
  { value: 'closed', labelKey: 'statusClosed' }
];

const STATUS_BADGE_CLASS = {
  open: 'badge-gray',
  under_investigation: 'badge-amber',
  resolved: 'badge-blue',
  closed: 'badge-teal'
};

const STATUS_LABEL_KEY = {
  open: 'statusOpen',
  under_investigation: 'statusUnderInvestigation',
  resolved: 'statusResolved',
  closed: 'statusClosed'
};

const RCAList = ({ organizationId, userId, userRole }) => {
  const { t } = useTranslation('rca');
  const { items, loading, error, filters, setFilters, refetch } = useRCA(organizationId);
  const [selectedRcaId, setSelectedRcaId] = useState(null);

  const handleEditRCA = (rcaId) => {
    setSelectedRcaId(rcaId);
  };

  const handleFormClose = () => {
    setSelectedRcaId(null);
    refetch();
  };

  if (!organizationId) {
    return (
      <div className="rca-list">
        <p className="rca-empty">{t('noOrganization')}</p>
      </div>
    );
  }

  return (
    <div className="rca-list">
      <div className="rca-list-header">
        <h2>{t('title')}</h2>
      </div>

      <div className="rca-list-filters">
        <select
          value={filters.status || ''}
          onChange={(e) => setFilters({ ...filters, status: e.target.value || undefined })}
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value || 'all'} value={opt.value}>
              {t(opt.labelKey)}
            </option>
          ))}
        </select>
        <label className="filter-checkbox">
          <input
            type="checkbox"
            checked={!!filters.assignedToMe}
            onChange={(e) =>
              setFilters({
                ...filters,
                assignedToMe: e.target.checked || undefined,
                userId: e.target.checked ? userId : undefined
              })
            }
          />
          {t('filterMine')}
        </label>
      </div>

      {error && (
        <div className="rca-error">
          <AlertTriangle size={16} /> {error.message || t('errorLoading')}
        </div>
      )}
      {loading && <div className="rca-loading">{t('loading')}</div>}

      {!loading && items.length === 0 && <p className="rca-empty">{t('noItems')}</p>}

      <div className="rca-items">
        {items.map((rca) => (
          <div
            key={rca.id}
            className="rca-card"
            onClick={() => handleEditRCA(rca.id)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handleEditRCA(rca.id)}
          >
            <div className="rca-card-header">
              <span className="rca-code">{rca.rca_code}</span>
              <span className={`rca-status-badge ${STATUS_BADGE_CLASS[rca.status] || 'badge-gray'}`}>
                {t(STATUS_LABEL_KEY[rca.status] || 'statusOpen')}
              </span>
            </div>
            <div className="rca-card-body">
              {rca.deviationId && (
                <span className="rca-deviation-ref">
                  <FileSearch size={14} /> {rca.deviationId}
                </span>
              )}
              {rca.assignedToName && (
                <span className="rca-assigned">
                  {t('assignedTo')}: {rca.assignedToName}
                </span>
              )}
              {rca.createdAt && (
                <span className="rca-date">
                  {rca.createdAt.toDate
                    ? rca.createdAt.toDate().toLocaleDateString()
                    : new Date(rca.createdAt).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {selectedRcaId && (
        <RCAForm
          organizationId={organizationId}
          userId={userId}
          userRole={userRole}
          rcaId={selectedRcaId}
          onSave={handleFormClose}
          onCancel={handleFormClose}
        />
      )}
    </div>
  );
};

export default RCAList;
